import { useState } from 'react';
import { Trash2, CheckCircle, AlertTriangle, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DataDeletionPage() {
  const [formData, setFormData] = useState({
    email: '',
    request_type: 'delete_all',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/deletion-request`, formData);
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Deletion request error:', error);
      // Still show success as the request was likely logged
      setSubmitSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="data-deletion-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#FAFAF5]">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div
            
            
            
            className="text-center"
          >
            <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
              <Trash2 size={28} className="text-white" />
            </div>
            <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">Your Rights</p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-black mb-6">
              Data <span className="italic">Deletion</span>
            </h1>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Under GDPR, you have the right to request the deletion of your personal data. 
              Use this form to submit a data deletion request.
            </p>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 bg-white border-b border-neutral-200">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div
              
              
              
              className="text-center p-6"
            >
              <div className="text-3xl mb-3">📧</div>
              <h3 className="font-semibold mb-2">Submit Request</h3>
              <p className="text-neutral-600 text-sm">Fill in the form below with your email address</p>
            </div>
            
            <div
              
              
              
              
              className="text-center p-6"
            >
              <div className="text-3xl mb-3">🔍</div>
              <h3 className="font-semibold mb-2">We Verify</h3>
              <p className="text-neutral-600 text-sm">We'll verify your identity and locate your data</p>
            </div>
            
            <div
              
              
              
              
              className="text-center p-6"
            >
              <div className="text-3xl mb-3">✅</div>
              <h3 className="font-semibold mb-2">Data Deleted</h3>
              <p className="text-neutral-600 text-sm">Your data is removed within 30 days</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-xl">
          {submitSuccess ? (
            <div
              
              
              className="text-center py-12 bg-[#FAFAF5] p-8"
              data-testid="deletion-success"
            >
              <CheckCircle size={64} className="mx-auto mb-6 text-green-600" />
              <h2 className="font-serif text-3xl font-semibold mb-4">Request Received</h2>
              <p className="text-neutral-600 mb-4">
                We've received your data deletion request. We will process it within 30 days 
                as required by GDPR regulations.
              </p>
              <p className="text-neutral-500 text-sm">
                You will receive a confirmation email once your data has been deleted.
              </p>
            </div>
          ) : (
            <div
              
              
            >
              {/* Warning Notice */}
              <div className="bg-yellow-50 border border-yellow-200 p-4 mb-8 flex items-start gap-3">
                <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-800 font-medium text-sm">Important Notice</p>
                  <p className="text-yellow-700 text-sm mt-1">
                    Data deletion is permanent and cannot be undone. This will remove all records 
                    associated with your email including reservations, enquiries, and newsletter subscriptions.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="deletion-form">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="deletion-email"
                    className="w-full bg-transparent border border-neutral-200 px-4 py-3 text-black focus:border-black focus:outline-none"
                    placeholder="Enter the email used for your submissions"
                    required
                  />
                  <p className="text-neutral-400 text-xs mt-2">
                    This should be the email address you used when making reservations, 
                    enquiries, or subscribing to our newsletter.
                  </p>
                </div>

                <div>
                  <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                    Request Type *
                  </label>
                  <select
                    value={formData.request_type}
                    onChange={(e) => setFormData({ ...formData, request_type: e.target.value })}
                    data-testid="deletion-type"
                    className="w-full bg-transparent border border-neutral-200 px-4 py-3 text-black focus:border-black focus:outline-none"
                    required
                  >
                    <option value="delete_all">Delete all my data</option>
                    <option value="delete_reservations">Delete reservation history only</option>
                    <option value="delete_newsletter">Unsubscribe from newsletter only</option>
                    <option value="delete_enquiries">Delete contact/franchise enquiries only</option>
                    <option value="export_data">Export my data (Data Portability)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs tracking-wider uppercase text-neutral-500 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    data-testid="deletion-message"
                    rows={3}
                    className="w-full bg-transparent border border-neutral-200 px-4 py-3 text-black focus:border-black focus:outline-none resize-none"
                    placeholder="Any additional details about your request (optional)"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="deletion-submit"
                  className="w-full bg-black text-white px-8 py-4 text-xs tracking-widest uppercase font-bold hover:bg-neutral-800 transition-all disabled:opacity-50 flex items-center justify-center"
                >
                  {isSubmitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      Submit Request
                      <Send size={16} className="ml-2" />
                    </>
                  )}
                </button>

                <p className="text-center text-neutral-500 text-xs">
                  We take your privacy seriously. For questions, contact us at{' '}
                  <a href="mailto:Enquiries@honeyimhomecoffee.com" className="text-black underline">
                    Enquiries@honeyimhomecoffee.com
                  </a>
                </p>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* GDPR Info */}
      <section className="py-16 bg-[#FAFAF5]">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="text-center">
            <h2 className="font-serif text-2xl font-semibold mb-4">Your Data Rights</h2>
            <p className="text-neutral-600 mb-8">
              Under the UK General Data Protection Regulation (GDPR), you have the following rights:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
              <div className="bg-white p-4">
                <h4 className="font-medium mb-1">Right to Access</h4>
                <p className="text-neutral-600 text-sm">Request a copy of your personal data</p>
              </div>
              <div className="bg-white p-4">
                <h4 className="font-medium mb-1">Right to Rectification</h4>
                <p className="text-neutral-600 text-sm">Request correction of inaccurate data</p>
              </div>
              <div className="bg-white p-4">
                <h4 className="font-medium mb-1">Right to Erasure</h4>
                <p className="text-neutral-600 text-sm">Request deletion of your data</p>
              </div>
              <div className="bg-white p-4">
                <h4 className="font-medium mb-1">Right to Portability</h4>
                <p className="text-neutral-600 text-sm">Receive your data in a portable format</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
